import React, { useCallback, useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import { StaticImageData } from 'next/image'
import { useEffectOnce } from 'react-use'

import photoInfra from '@web/libs/media/photoInfra'
import { ThumbnailType } from '@web/libs/media/photoInfra.constant'
import { neloLogger } from '@web/libs/utils'

import { convertImageUrl, PupKeywordEmoji } from './convertImageUrl'
import FallbackImage from './FallbackImage'
import styles from './index.module.scss'

const TRANSITION_INIT_STYLE = { opacity: 0 }
const TRANSITION_COMPLETE_STYLE = {
  opacity: 1,
  WebkitTransition: 'opacity 0.3s ease-in',
  Transition: 'opacity 0.3s ease-in',
}

export interface ImageProps {
  src: string | StaticImageData | PupKeywordEmoji
  width?: number | string
  height?: number | string
  alt?: string
  className?: string
  lazyLoading?: boolean
  objectPosition?: string
  onError?: (event: React.SyntheticEvent<HTMLImageElement, Event> | Event | string) => void
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement, Event> | Event) => void
  thumbnailType?: ThumbnailType | null
  isPlaybackError?: boolean
  isSSR?: boolean // Image를 server side에서 그리는지 여부
  transition?: boolean // transition style 사용할지 여부
}

/**
 * `thumbnailType` 사용 권장
 */
const Image = ({
  src,
  width = '',
  height = '',
  alt = '',
  className = '',
  lazyLoading = false,
  objectPosition = '',
  thumbnailType,
  onLoad,
  isPlaybackError = false,
  isSSR = false,
  transition = true,
}: ImageProps) => {
  const url = convertImageUrl(src)

  const [error, setError] = useState<boolean>(false)

  const [style, setStyle] = useState<React.CSSProperties>(
    isSSR
      ? {}
      : {
          ...TRANSITION_INIT_STYLE,
        },
  )
  const imageRef = useRef<HTMLImageElement>(null)

  const onLoadImage = useCallback((event?: React.SyntheticEvent<HTMLImageElement, Event> | Event) => {
    if (event) onLoad?.(event)
    setStyle({ ...TRANSITION_COMPLETE_STYLE, objectPosition })
  }, [])

  const onErrorImage = useCallback(() => {
    neloLogger('warn', '[Image] 이미지 로드 중 에러가 발생했습니다.', {
      url,
      width,
      height,
      thumbnailType,
      thumbnailUrl,
    })
    setError(true)
  }, [])

  useEffectOnce(() => {
    if (imageRef.current?.complete) {
      onLoadImage()
    }
  })

  useEffect(() => {
    if (error) {
      setStyle(TRANSITION_COMPLETE_STYLE)
    }
  }, [error])

  const thumbnailUrl = thumbnailType
    ? photoInfra.convertThumbnail({
        url,
        type: thumbnailType,
      })
    : url

  if (error) {
    return <FallbackImage className={className} height={height} width={width} />
  }

  return (
    <>
      <img
        src={thumbnailUrl}
        className={classNames(styles.img, className)}
        width={width}
        ref={imageRef}
        height={height}
        alt={alt}
        loading={lazyLoading ? 'lazy' : undefined}
        onLoad={onLoadImage}
        onError={onErrorImage}
        style={transition ? style : {}}
      />
      {isPlaybackError && <FallbackImage width={width} height={height} isDimmed />}
    </>
  )
}

export { PupKeywordEmoji } from './convertImageUrl'

export default Image
